import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import HardBreak from "@tiptap/extension-hard-break";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import { useEffect, useImperativeHandle, forwardRef, useRef } from "react";
import { TranslationService } from "../../../services/TranslationService";
import { FontSize } from "./extensions/FontSize";
import "./EmailEditorTipTap.css";

export interface EmailEditorTipTapRef {
  insertContent: (content: string) => void;
  setContent: (content: string) => void;
  getHTML: () => string;
  focus: () => void;
  chain: () => any;
  setCursorToStart: () => void;
}

interface EmailEditorTipTapProps {
  content: string;
  onChange: (html: string) => void;
  onPasteFiles?: (files: File[]) => void;
  placeholder?: string;
}

export const EmailEditorTipTap = forwardRef<
  EmailEditorTipTapRef,
  EmailEditorTipTapProps
>(({ content, onChange, onPasteFiles, placeholder }, ref) => {
  // Ref para rastrear el último contenido sincronizado
  const lastSyncedContent = useRef<string>(content);
  // Ref para rastrear si el usuario está editando activamente
  const isUserEditingRef = useRef<boolean>(false);
  // Timeout para resetear el flag de edición
  const editingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Ref para acceder al editor dentro de editorProps (no disponible en el closure de useEditor)
  const editorInstanceRef = useRef<ReturnType<typeof useEditor>>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6]
        }
      }),
      HardBreak,
      Underline,
      TextStyle,
      Color,
      FontSize,
      FontFamily.configure({
        types: ["textStyle"]
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"]
      }),
      Link.configure({
        openOnClick: false,
        autolink: false,
        linkOnPaste: false,
        HTMLAttributes: {
          target: "_self"
        }
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
        HTMLAttributes: {
          class: "email-image"
        }
      })
    ],
    content,
    editorProps: {
      attributes: {
        class: "email-editor-content",
        style: "outline: none !important; box-shadow: none !important;"
      },
      handlePaste: (view, event) => {
        if (onPasteFiles) {
          const items = Array.from(event.clipboardData?.items || []);
          const files = items
            .filter((item) => item.kind === "file")
            .map((item) => item.getAsFile())
            .filter((file): file is File => file !== null);

          if (files.length > 0) {
            event.preventDefault();
            onPasteFiles(files);
            return true;
          }
        }

        // Si el HTML pegado contiene mailto:, reemplazar esos <a> por su texto interno
        // manteniendo el resto del formato (bold, italic, etc.)
        const htmlData = event.clipboardData?.getData("text/html");
        if (htmlData && htmlData.includes("mailto:")) {
          event.preventDefault();
          const cleaned = htmlData.replace(
            /<a[^>]*href="mailto:[^"]*"[^>]*>([\s\S]*?)<\/a>/gi,
            "$1"
          );
          editorInstanceRef.current?.commands.insertContent(cleaned);
          return true;
        }

        return false;
      }
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const formattedHtml = formatHtmlLikeDraft(html);

      // Validar formateo en desarrollo
      validateFormatting(html);

      lastSyncedContent.current = formattedHtml;
      // Marcar que el usuario está editando
      isUserEditingRef.current = true;
      // Resetear el flag después de un breve delay
      if (editingTimeoutRef.current) {
        clearTimeout(editingTimeoutRef.current);
      }
      editingTimeoutRef.current = setTimeout(() => {
        isUserEditingRef.current = false;
      }, 300); // 300ms de delay para considerar que el usuario terminó de editar
      onChange(formattedHtml);
    }
  });

  // Sincronizar la ref interna con la instancia del editor
  editorInstanceRef.current = editor;

  // Exponer métodos del editor mediante ref
  useImperativeHandle(
    ref,
    () => ({
      insertContent: (content: string) => {
        if (editor) {
          editor.chain().focus().insertContent(content).run();
        }
      },
      setContent: (content: string) => {
        if (editor) {
          editor.commands.setContent(content, { emitUpdate: false });
        }
      },
      getHTML: () => {
        const html = editor?.getHTML() || "";
        return formatHtmlLikeDraft(html);
      },
      focus: () => {
        if (editor) {
          // Intentar hacer focus usando el comando de TipTap
          editor.commands.focus();
          // También intentar hacer focus directamente en el elemento DOM como respaldo
          // Esto asegura que el cursor aparezca incluso si el comando de TipTap no funciona
          const editorElement = editor.view.dom as HTMLElement;
          if (editorElement) {
            // Usar requestAnimationFrame para asegurar que el DOM esté listo
            requestAnimationFrame(() => {
              editorElement.focus();
              // Si el editor tiene un elemento editable interno, también hacer focus ahí
              const editableElement = editorElement.querySelector(
                ".ProseMirror"
              ) as HTMLElement;
              if (editableElement) {
                editableElement.focus();
              }
            });
          }
        }
      },
      chain: () => {
        return editor?.chain();
      },
      setCursorToStart: () => {
        if (editor) {
          // Solo posicionar el cursor al inicio sin hacer foco automáticamente
          // Esto evita interrumpir al usuario si ya está escribiendo
          editor.chain().setTextSelection(0).run();
        }
      }
    }),
    [editor]
  );

  // Inicializar el ref cuando el editor se crea
  useEffect(() => {
    if (editor) {
      const html = editor.getHTML();
      lastSyncedContent.current = formatHtmlLikeDraft(html);
    }
  }, [editor]);

  // Cleanup del timeout cuando el componente se desmonte
  useEffect(() => {
    return () => {
      if (editingTimeoutRef.current) {
        clearTimeout(editingTimeoutRef.current);
      }
    };
  }, []);

  // Sincronizar contenido externo
  useEffect(() => {
    if (editor && content !== lastSyncedContent.current) {
      // No actualizar si el usuario está editando activamente para evitar pérdida de foco
      if (isUserEditingRef.current) {
        return;
      }

      const currentHtml = editor.getHTML();
      // Solo actualizar si el contenido realmente cambió y es diferente al actual
      if (content !== currentHtml) {
        // Verificar si el editor tiene foco antes de actualizar
        const hasFocus = editor.isFocused;
        editor.commands.setContent(content, { emitUpdate: false });
        lastSyncedContent.current = content;
        // Restaurar el foco si lo tenía antes
        if (hasFocus) {
          // Usar setTimeout para restaurar el foco después de que React actualice el DOM
          setTimeout(() => {
            editor.commands.focus();
          }, 0);
        }
      }
    }
  }, [content, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div
      className="email-editor-wrapper"
      style={{ outline: "none", boxShadow: "none" }}
    >
      <div
        className="email-editor-content-wrapper"
        style={{ outline: "none", boxShadow: "none" }}
        onClick={() => {
          if (editor) {
            editor.commands.focus();
          }
        }}
      >
        <EditorContent editor={editor} />
      </div>
      <EmailToolbar editor={editor} />
    </div>
  );
});

EmailEditorTipTap.displayName = "EmailEditorTipTap";

/**
 * Formatea el HTML igual que draftjs-to-html, agregando \n después de cada tag de cierre
 * para que el backend pueda formatear bien el contenido.
 *
 * Maneja correctamente:
 * - Párrafos vacíos: <p></p> → <p></p>\n
 * - Párrafos con <br>: <p><br></p> → <p><br></p>\n
 * - Párrafos con <br/>: <p><br/></p> → <p><br/></p>\n
 * - Párrafos con contenido: <p>texto</p> → <p>texto</p>\n
 *
 * Ejemplo de salida: "<p><br>Alan test</p>\n<p>enter</p>\n<p>otro test</p>\n"
 */
export function formatHtmlLikeDraft(html: string): string {
  if (!html) return "";

  // Agregar \n después de cada tag de cierre de bloque
  // Esto incluye: </p>, </div>, </h1>, </h2>, </h3>, </h4>, </h5>, </h6>, </li>, </ul>, </ol>, </blockquote>
  // IMPORTANTE: Esto también funciona para párrafos vacíos como <p></p> o <p><br></p>
  let formatted = html
    // Agregar \n después de tags de cierre de bloque (incluye párrafos vacíos)
    // El regex captura cualquier </p> sin importar si tiene contenido o no
    .replace(/(<\/p>)/gi, "$1\n")
    .replace(/(<\/div>)/gi, "$1\n")
    .replace(/(<\/h[1-6]>)/gi, "$1\n")
    .replace(/(<\/li>)/gi, "$1\n")
    .replace(/(<\/ul>)/gi, "$1\n")
    .replace(/(<\/ol>)/gi, "$1\n")
    .replace(/(<\/blockquote>)/gi, "$1\n")
    // Limpiar múltiples \n consecutivos (máximo 2 seguidos)
    // Esto preserva los saltos de línea entre párrafos pero evita demasiados espacios
    .replace(/\n{3,}/g, "\n\n")
    // Eliminar espacios en blanco al inicio, pero preservar \n al final si existe
    .replace(/^\s+/, "");

  // Si el string no termina con \n, agregar \n al final (igual que draftjs-to-html)
  // Esto asegura que incluso párrafos vacíos al final tengan su \n
  if (formatted && !formatted.endsWith("\n")) {
    formatted += "\n";
  }

  return formatted;
}

/**
 * Función de validación para verificar que el formateo funciona correctamente
 * con párrafos vacíos. Solo se usa en desarrollo para debugging.
 */
function validateFormatting(html: string): void {
  if (process.env.NODE_ENV === "development") {
    const formatted = formatHtmlLikeDraft(html);

    // Validar que todos los </p> tengan \n después
    const pTags = formatted.match(/<\/p>/gi);
    const pTagsWithNewline = formatted.match(/<\/p>\n/g);

    if (pTags && pTags.length > 0) {
      const allHaveNewline = pTags.length === (pTagsWithNewline?.length || 0);
      if (!allHaveNewline) {
        console.warn(
          "[EmailEditorTipTap] Algunos párrafos no tienen \\n después del cierre:",
          {
            totalPTags: pTags.length,
            pTagsWithNewline: pTagsWithNewline?.length || 0,
            html: formatted.substring(0, 200)
          }
        );
      }
    }

    // Validar párrafos vacíos específicamente
    const emptyParagraphs =
      formatted.match(/<p><\/p>/gi) || formatted.match(/<p><br\s*\/?><\/p>/gi);
    if (emptyParagraphs) {
      const emptyParagraphsWithNewline =
        formatted.match(/<p><\/p>\n/gi) ||
        formatted.match(/<p><br\s*\/?><\/p>\n/gi);
      if (
        emptyParagraphs.length !== (emptyParagraphsWithNewline?.length || 0)
      ) {
        console.warn(
          "[EmailEditorTipTap] Algunos párrafos vacíos no tienen \\n:",
          {
            emptyParagraphs: emptyParagraphs.length,
            emptyParagraphsWithNewline: emptyParagraphsWithNewline?.length || 0
          }
        );
      }
    }
  }
}

// Componente de Toolbar personalizado
const EmailToolbar = ({ editor }: { editor: any }) => {
  const { translate } = TranslationService;

  if (!editor) {
    return null;
  }

  return (
    <div className="email-toolbar border-bottom p-2 d-flex flex-wrap gap-2 align-items-center">
      {/* Font Size */}
      <select
        className="form-select form-select-sm"
        style={{ width: "auto" }}
        onChange={(e) => {
          const size = e.target.value;
          if (size) {
            editor.chain().focus().setFontSize(size).run();
          } else {
            editor.chain().focus().unsetFontSize().run();
          }
        }}
        value={editor.getAttributes("textStyle").fontSize || ""}
      >
        <option value="">{translate.FontSize}</option>
        <option value="8">8</option>
        <option value="10">10</option>
        <option value="12">12</option>
        <option value="14">14</option>
        <option value="18">18</option>
        <option value="24">24</option>
        <option value="36">36</option>
      </select>

      {/* Font Family */}
      <select
        className="form-select form-select-sm"
        style={{ width: "auto" }}
        onChange={(e) => {
          const fontFamily = e.target.value;
          if (fontFamily) {
            editor.chain().focus().setFontFamily(fontFamily).run();
          }
        }}
        value={editor.getAttributes("textStyle").fontFamily || ""}
      >
        <option value="">{translate.FontFamily}</option>
        <option value="Arial">Arial</option>
        <option value="Georgia">Georgia</option>
        <option value="Impact">Impact</option>
        <option value="Tahoma">Tahoma</option>
        <option value="Times New Roman">Times New Roman</option>
        <option value="Verdana">Verdana</option>
      </select>

      {/* Color Picker */}
      <input
        type="color"
        className="form-control form-control-color"
        style={{ width: "40px", height: "38px", cursor: "pointer" }}
        onInput={(e) => {
          const color = (e.target as HTMLInputElement).value;
          editor.chain().focus().setColor(color).run();
        }}
        title={translate.TextColor || "Color de texto"}
      />

      {/* Bold, Italic, Underline */}
      <div className="btn-group" role="group">
        <button
          type="button"
          className={`btn btn-sm btn-outline-secondary ${editor.isActive("bold") ? "active" : ""}`}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title={translate.Bold || "Negrita"}
        >
          <i className="fas fa-bold"></i>
        </button>
        <button
          type="button"
          className={`btn btn-sm btn-outline-secondary ${editor.isActive("italic") ? "active" : ""}`}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title={translate.Italic || "Cursiva"}
        >
          <i className="fas fa-italic"></i>
        </button>
        <button
          type="button"
          className={`btn btn-sm btn-outline-secondary ${editor.isActive("underline") ? "active" : ""}`}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title={translate.Underline || "Subrayado"}
        >
          <i className="fas fa-underline"></i>
        </button>
      </div>

      {/* Text Align */}
      <div className="btn-group" role="group">
        <button
          type="button"
          className={`btn btn-sm btn-outline-secondary ${editor.isActive({ textAlign: "left" }) ? "active" : ""}`}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          title={translate.AlignLeft || "Izquierda"}
        >
          <i className="fas fa-align-left"></i>
        </button>
        <button
          type="button"
          className={`btn btn-sm btn-outline-secondary ${editor.isActive({ textAlign: "center" }) ? "active" : ""}`}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          title={translate.AlignCenter || "Centro"}
        >
          <i className="fas fa-align-center"></i>
        </button>
        <button
          type="button"
          className={`btn btn-sm btn-outline-secondary ${editor.isActive({ textAlign: "right" }) ? "active" : ""}`}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          title={translate.AlignRight || "Derecha"}
        >
          <i className="fas fa-align-right"></i>
        </button>
      </div>

      {/* Lists */}
      <div className="btn-group" role="group">
        <button
          type="button"
          className={`btn btn-sm btn-outline-secondary ${editor.isActive("bulletList") ? "active" : ""}`}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title={translate.BulletList || "Lista con viñetas"}
        >
          <i className="fas fa-list-ul"></i>
        </button>
        <button
          type="button"
          className={`btn btn-sm btn-outline-secondary ${editor.isActive("orderedList") ? "active" : ""}`}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title={translate.NumberedList || "Lista numerada"}
        >
          <i className="fas fa-list-ol"></i>
        </button>
      </div>

      {/* Link */}
      <div className="btn-group" role="group">
        <button
          type="button"
          className={`btn btn-sm btn-outline-secondary ${editor.isActive("link") ? "active" : ""}`}
          onClick={() => {
            const url = window.prompt(translate.EnterURL || "Ingrese la URL:");
            if (url) {
              editor.chain().focus().setLink({ href: url }).run();
            }
          }}
          title={translate.InsertLink || "Insertar enlace"}
        >
          <i className="fas fa-link"></i>
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => editor.chain().focus().unsetLink().run()}
          disabled={!editor.isActive("link")}
          title={translate.RemoveLink || "Quitar enlace"}
        >
          <i className="fas fa-unlink"></i>
        </button>
      </div>

      {/* Undo/Redo */}
      <div className="btn-group" role="group">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title={translate.Undo || "Deshacer"}
        >
          <i className="fas fa-undo"></i>
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title={translate.Redo || "Rehacer"}
        >
          <i className="fas fa-redo"></i>
        </button>
      </div>
    </div>
  );
};
